import React, { Dispatch, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { replaceItemAtIndex } from 'utils/common';


type CheckboxListProps<T> = {
    items: T[],
    formatter: (v: T) => ReactNode,
    checked: boolean[],
    setChecked: Dispatch<React.SetStateAction<boolean[]>>,
}


const CheckboxList = <T,>({setChecked, checked, items, formatter}: CheckboxListProps<T>) => {
    const {t} = useTranslation();
    const lastChecked = useRef<number | null>(null);

    if (items.length === 0)
        return null;

    const checkAll = (event: React.ChangeEvent<HTMLInputElement>) =>
        setChecked(old => old.map(() => event.target.checked));

    const checkOne = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = (e.target as HTMLInputElement).checked;

        if (lastChecked.current !== null && (e.nativeEvent as KeyboardEvent).shiftKey) {
            setChecked(old => {
                const start = Math.min(lastChecked.current!, idx);
                const end = Math.max(lastChecked.current!, idx);
                const temp = [...old];

                for (let i = start; i <= end; i++) {
                    temp[i] = checked;
                }

                return temp;
            });
        } else {
            setChecked(old => replaceItemAtIndex(old, idx, checked));
        }

        lastChecked.current = idx;
    };

    const countChecked = checked.filter(Boolean).length;

    return (
        <FormGroup>
            <FormControlLabel
                sx={{
                    userSelect: 'none',
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'white',
                    zIndex: 2,
                    '& .MuiFormControlLabel-label': {
                        width: '100%',
                    }
                }}
                label={<Box display="flex" justifyContent="space-between">
                    <Box>{t('check_all')}</Box>
                    <Box>{countChecked > 0 && t('n_of_m', {n: countChecked, m: checked.length})}</Box>
                </Box>}
                control={
                    <Checkbox
                        checked={checked.every(Boolean)}
                        indeterminate={new Set(checked).size > 1}
                        onChange={checkAll}
                    />
                }
            />
            {items.map((item, i) => (
                <FormControlLabel
                    sx={{
                        userSelect: 'none',
                        '& .MuiFormControlLabel-label': {
                            width: '100%',
                        }
                    }}
                    key={i}
                    label={formatter(item)}
                    control={
                        <Checkbox
                            name={'' + i}
                            checked={checked[i]}
                            onChange={e => checkOne(i, e)}
                        />
                    }
                />
            ))}
        </FormGroup>
    );
};

export default CheckboxList;
